import type { Dispatch, FC, SetStateAction } from "react";

import { Box, Slider, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEditCampaign } from "../../../context/EditCampaignContext";

interface BrandingIconsProps {
  onIconColor: Dispatch<SetStateAction<string | undefined>>;
  onIconBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  onIconBackgroundRadius: Dispatch<SetStateAction<number | undefined>>;
  iconColor?: string;
  backgroundColor?: string;
  backgroundRadius?: number;
}

export const BrandingIcons: FC<BrandingIconsProps> = ({
  onIconColor,
  onIconBackgroundColor,
  onIconBackgroundRadius,
  backgroundColor,
  backgroundRadius,
  iconColor,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setDetectedChange } = useEditCampaign();

  const sliderStyles = (disabled: boolean) => {
    return {
      color: "#FFCF96",
      "& .MuiSlider-track": {
        border: "none",
        color: disabled ? theme.palette.natural.t3 : undefined,
      },
      "& .MuiSlider-thumb": {
        width: 20,
        height: 20,
        borderRadius: 0,
        background: disabled
          ? theme.palette.natural.t3
          : theme.palette.main.gradient.primary.p2,
        "&:before": {
          boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
        },
        "&:hover, &.Mui-focusVisible, &.Mui-active": {
          boxShadow: "none",
        },
      },
    };
  };

  return (
    <Stack direction='column' gap={1}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170 }}>
          {t("campaign.branding.icons.iconColor")}:
        </Typography>
        <input
          value={iconColor ?? "#7b76f0"}
          type='color'
          style={{
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
            border: "none",
            width: 100,
            height: 30,
            cursor: "pointer",
            padding: 0,
            backgroundColor: "transparent",
          }}
          onChange={e => {
            onIconColor(e.currentTarget.value);
            setDetectedChange(true);
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170 }}>
          {t("campaign.branding.icons.backgroundColor")}:
        </Typography>
        <input
          type='color'
          value={backgroundColor ?? "#ffffff"}
          style={{
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
            border: "none",
            width: 100,
            height: 30,
            cursor: "pointer",
            padding: 0,
            backgroundColor: "transparent",
          }}
          onChange={e => {
            onIconBackgroundColor(e.currentTarget.value);
            setDetectedChange(true);
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "30px",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170, textWrap: "nowrap" }}>
          {t("campaign.branding.icons.backgroundRadius")}:
        </Typography>
        <Slider
          aria-label='Volume'
          sx={{
            ...sliderStyles(false),
            maxWidth: 200,
          }}
          min={0}
          max={50}
          onChange={(_, value) => {
            onIconBackgroundRadius(value as number);
            setDetectedChange(true);
          }}
          value={backgroundRadius ?? 0}
          defaultValue={backgroundRadius ?? 0}
        />
      </Box>
    </Stack>
  );
};
